import { useEffect } from 'react';
import { Box, Button, Flex, Loader, Text, TextInput } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { showNotification } from '@shared/utils';
import { QueryKey } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';
import { useForm, zodResolver } from '@mantine/form';
import {
  AIConstantShowModalFormSchema,
  type AIConstantShowModalFormValues,
} from './types';
import { useGetAIConstant, useUpdateAiConstant } from '@entities/aiConstants';
import classes from './aIConstantShowModal.module.scss';
import { MODAL_SIZE_MEDIUM } from '@shared/ui/modal';

const MODAL_ID = 'ai-constant-show-modal';

type AIConstantShowModalComponentProps = {
  id: number;
};

const AIConstantShowModalComponent = ({
  id,
}: AIConstantShowModalComponentProps) => {
  const queryClient = useQueryClient();

  const { data: aiConstant, isLoading } = useGetAIConstant(id);
  const { mutate: updateAiConstant, isPending } = useUpdateAiConstant(id);

  const form = useForm({
    initialValues: {
      code: '',
      value: '',
    },
    validate: zodResolver(AIConstantShowModalFormSchema),
  });

  useEffect(() => {
    if (isLoading || !aiConstant) {
      return;
    }

    form.setValues({
      code: aiConstant.code,
      value: aiConstant.value,
    });
  }, [aiConstant]);

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleSubmit = (values: AIConstantShowModalFormValues) => {
    updateAiConstant(
      {
        code: values.code,
        value: values.value,
      },
      {
        onSuccess: data => {
          queryClient.setQueryData([QueryKey.GET_AI_CONSTANT, id], data);
          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_AI_CONSTANTS],
          });

          showNotification({
            variant: 'info',
            title: 'Константа ИИ изменена',
            message: aiConstant?.name,
          });
        },
        onError: () => {
          showNotification({
            variant: 'error',
            title: 'Ошибка при обновлении константы',
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Flex justify='center' align='center' h={320} w='100%'>
        <Loader />
      </Flex>
    );
  }

  return (
    <Flex direction='column' gap={16}>
      <Text size='lg' fw={500} c='tertiary.9'>
        Количество запросов для перевода диалога в статус «требует участия»
      </Text>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction='column' gap={16}>
          <Box>
            <Text
              component='label'
              htmlFor='password'
              className={classes.label}
            >
              Код
            </Text>

            <TextInput
              w='100%'
              name='code'
              variant='filled'
              style={{
                marginTop: '0.375rem',
              }}
              {...form.getInputProps('code')}
            />
          </Box>

          <Box>
            <Text
              component='label'
              htmlFor='password'
              className={classes.label}
            >
              Значение константы
            </Text>

            <TextInput
              w='100%'
              name='value'
              variant='filled'
              style={{
                marginTop: '0.375rem',
              }}
              {...form.getInputProps('value')}
            />
          </Box>
        </Flex>

        <Flex pt={24} gap={8}>
          <Button variant='outline' onClick={handleClose}>
            Отмена
          </Button>
          <Button variant='primary' type='submit' loading={isPending}>
            Сохранить
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};

export const useAIConstantShowModal = () => {
  const openAIConstantShowModal = (id: string) =>
    openModal({
      modalId: MODAL_ID,
      title: `Редактировать константу #${id}`,
      children: <AIConstantShowModalComponent id={Number(id)} />,
      size: MODAL_SIZE_MEDIUM,
    });

  return { openAIConstantShowModal };
};
